import { TAnalyticsEventAction } from './analytics.types';
import { LogEvents } from '~/services/fullstory/log-events';
import { User } from '~/models/user.model';
import {
    AnalyticsAdapter,
    AnalyticsAdapterProps
} from '~/services/adapters/analytics.adapter';
import { sendFullStoryEvent } from '~/services/base-api.service';
import { getScreenResolution } from '~/services/screen.service';
import { IAnalyticsService } from '~/services/interfaces/IAnalyticsService';
import { isViewerMode } from '~/utils/viewer-mode-flow.utils';
import {
    getMobileDevice,
    getMobileOs,
    isInAppBrowser
} from '~/utils/device.utils';
import { extractDomain } from '~/utils/url.utils';
import { buildSegmentIdentifyParams } from '~/components/Analytics/analytics.utils';
import { isReactFirst } from '~/utils/environment.utils';
import { ILogger } from '@honeybook/core';
import type { Analytics } from '@segment/analytics-next';

export interface IEventProperties {
    hb_version?: string;
    hb_api_version?: number;
    screen_resolution?: string;
    app_path?: string;
    user_id?: string;
    company_id?: string;
    action?: TAnalyticsEventAction;
    type?: string;
    source?: string;

    [x: string]: any;
}

export interface IAuthorizedAnalyticsService
    extends IAnalyticsService,
        AnalyticsAdapter {
    identifyUser(
        user?: Partial<User>,
        integrations?: Record<string, boolean>
    ): ReturnType<Analytics['identify']>;

    unidentify(): void;

    reportEvent(
        eventName: string,
        eventProps?: Record<string, any>,
        integrations?: Record<string, boolean>
    ): void;

    reportIterableEvent(
        eventName: string,
        eventProps?: Record<string, any>,
        reportingUser?: any
    ): void;

    addSessionProperty: (
        propertyKey: string,
        propertyValue: string | boolean
    ) => void;

    overrideSegmentAnonymousId(id: string, traits?: Record<string, any>): void;
}

export const createEventTitle = (
    eventName: string,
    properties: IEventProperties
) => {
    if (!properties?.action) {
        return eventName;
    }
    return `${properties.action}: ${eventName}`;
};

type AnalyticsServiceProps = {
    logger: ILogger;
} & AnalyticsAdapterProps;

export class AnalyticsService
    extends AnalyticsAdapter
    implements IAuthorizedAnalyticsService
{
    isGATrackerInit: boolean;
    segmentDefaultIntegrations = {
        All: true,
        Iterable: false
    };
    initialReferrer: string;
    superProperties;
    _reportMixpanelViaSegment;
    logger: ILogger;
    sessionProperties: Record<string, string | boolean> = {};
    hbAnonymousId: string;

    reportMixpanelViaSegment = () => {
        this._reportMixpanelViaSegment ||=
            this.configProvider.config.block_mixpanel &&
            this.configProvider.config.block_mixpanel_react;
        return this._reportMixpanelViaSegment;
    };

    constructor({ logger, ...rest }: AnalyticsServiceProps) {
        super(rest);
        this.logger = logger;

        this.isGATrackerInit = false;
        this.initialReferrer = document.referrer;
    }

    getDefaultProperties(): IEventProperties {
        return {
            hb_version: this.configProvider.config.version,
            hb_api_version: this.configProvider.config.api_version,
            screen_resolution: getScreenResolution(this.logger),
            is_in_app_browser: isInAppBrowser(),
            is_react_first: isReactFirst(),
            platform: 'react',
            hb_anonymous_id: this.hbAnonymousId
        };
    }

    addSessionProperty(propertyKey: string, value: string | boolean) {
        this.sessionProperties[propertyKey] = value;
    }

    _getDeviceProperties() {
        const { currUser } = this.userGetter;
        return {
            $screen_width: window.screen.width,
            $screen_height: window.screen.height,
            $device: getMobileDevice(),
            $initial_referrer: this.initialReferrer,
            $initial_referring_domain: extractDomain(this.initialReferrer),
            $referring_domain: extractDomain(document.referrer),
            $user_id: currUser?._id
        };
    }

    _setSuperProperties() {
        const user = this.userGetter?.currUser;
        if (!!this.superProperties || !user) {
            return;
        }
        const company = user && user.company;
        let superPropertiesObj = this.getUserTraits();

        if (company) {
            const companyProperties = {
                user_company_owner_id: company.owner?._id,
                user_company_owner_email: company.owner?.email,
                user_company_team_members_count: company.team_members?.length
            };
            superPropertiesObj = { ...superPropertiesObj, companyProperties };
        }

        if (user) {
            const statsigProperties: {
                statsig_stable_id?: string;
                account_id: string;
                statsigCustomIDs: string[];
            } = {
                account_id: user.account_id,
                statsigCustomIDs: ['account_id', user.account_id]
            };

            if (user.statsig_stable_id) {
                statsigProperties.statsig_stable_id = user.statsig_stable_id;
            }
            superPropertiesObj = {
                ...superPropertiesObj,
                ...statsigProperties
            };
        }

        if (this.shouldTrackMixpanel() && !this.reportMixpanelViaSegment()) {
            this.registerMixpanel(superPropertiesObj);
        }

        this.superProperties = superPropertiesObj;
    }

    track(eventName: string, properties: IEventProperties) {
        const title = createEventTitle(eventName, properties);
        const allProperties = {
            ...this.getDefaultProperties(),
            ...this.sessionProperties,
            ...properties
        };
        const mobileOsParams = getMobileOs();
        if (mobileOsParams.mobileOs) {
            allProperties.mobile_os = mobileOsParams.mobileOs;
            if (mobileOsParams.iosVersion) {
                allProperties.ios_version = mobileOsParams.iosVersion;
            } else if (mobileOsParams.androidVersion) {
                allProperties.android_version = mobileOsParams.androidVersion;
            }
        }

        const { currUser } = this.userGetter;
        if (currUser) {
            allProperties.is_bookkeeper_mode = currUser.isInBookkeeperMode;
        }

        if (isViewerMode()) {
            allProperties.user_is_viewer_mode = true;
        }

        if (!this.superProperties) {
            this._setSuperProperties();
        }
        const segmentEventProperties = {
            ...(this.superProperties || {}),
            ...this._getDeviceProperties(),
            ...allProperties
        };
        if (this.shouldTrackMixpanel()) {
            sendFullStoryEvent(title, allProperties);
            if (!this.reportMixpanelViaSegment()) {
                this.trackMixpanel(title, allProperties);
            } else {
                this.reportEvent(title, segmentEventProperties, {
                    All: false,
                    Mixpanel: true,
                    Statsig: true,
                    'Mixpanel (Actions)': true
                });
            }
        } else {
            this.logger.debug({ title, ...segmentEventProperties });
        }
    }

    unidentify() {
        if (!this.reportMixpanelViaSegment()) {
            window.mixpanel.reset();
            if (window.mixpanel.cookie) {
                window.mixpanel.cookie.clear();
            }
        }
        this.superProperties = undefined;
        this.anonymizeUser();
    }

    anonymizeUser() {
        if (this.isSegmentAvailable()) {
            window.analytics.reset();
        }
    }

    getUserTraits() {
        let args = {};
        const user = this.userGetter.currUser;
        if (user) {
            args = {
                id: user._id,
                $id: user._id,
                email: user.email,
                $email: user.email,
                first_name: user.first_name,
                $first_name: user.first_name,
                last_name: user.last_name,
                $last_name: user.last_name,
                name: user.full_name,
                $name: user.full_name,
                user_type: user.system_user_type,
                user_job_title: user.user_type_name,
                user_hb_system_type: user.hb_user_type,
                lifecyclestage: 'other',
                'activated?': !!user.isActivated?.(), // Aligned with mobile, do not change without coordination
                is_subscribed: user.companyHasSubscription,
                user_status: user.getUserStatus?.(),
                flows_enabled: !!user.isFlowFlagEnabled,
                legacy_files_enabled: !user.isBlockFilesFlagEnabled,
                trial_start_date: user.getTrialStartDate?.(),
                initial_intent: user.user_intent,
                user_is_hb_admin: user.is_hb_admin
                    ? !!user.is_hb_admin
                    : undefined
            };

            if (user.isVendor?.()) {
                let vendorArgs = {};
                if (user.company) {
                    vendorArgs = {
                        user_company_type: user.company.company_type_name,
                        user_company_name: user.company.company_name,
                        pending_stripe_migration: user.is_company_owner
                            ? user.company.pending_stripe_migration
                            : undefined,
                        predicted_member_segment:
                            user.account?.predicted_member_segment_measurement,
                        bucket_number:
                            typeof user.bucket_number === 'number'
                                ? user.bucket_number
                                : undefined
                    };
                }
                args = { ...args, ...vendorArgs };
            }
        }

        return args;
    }

    identifyUser(user?: User, integrations?: Record<string, boolean>) {
        const _user = user || this.userGetter.currUser;
        if (!_user) {
            return;
        }

        const { userTraits, options } = buildSegmentIdentifyParams(
            _user,
            integrations
        );
        if (this.isSegmentAvailable()) {
            return this.identifySegment(
                this.getUserId(user),
                userTraits,
                options
            );
        } else {
            // eslint-disable-next-line no-console
            console.log('[tracking: Segment] identifyUser');
        }
    }

    reportEvent(
        eventName: string,
        eventProps: Record<string, any> = {},
        integrations?: Record<string, boolean>
    ) {
        if (this.shouldTrackSegments()) {
            if (!this.isSegmentAvailable()) {
                setTimeout(() => {
                    this.reportEvent(eventName, eventProps, integrations);
                }, 200);
                return;
            }
            const configurations = {
                integrations: integrations || this.segmentDefaultIntegrations
            };
            return this.trackSegment(eventName, eventProps, configurations);
        } else {
            // eslint-disable-next-line no-console
            console.log(
                `[tracking: Segment] ${eventName} |  ${JSON.stringify(
                    eventProps,
                    null,
                    2
                )}`
            );
        }
    }

    reportIterableEvent(
        eventName: string,
        eventProps: Record<string, any> = {},
        reportingUser
    ) {
        const user = reportingUser || {};
        const integrations = {
            All: false,
            Iterable: true
        };

        if (this.isSegmentAvailable()) {
            this.identifySegment(
                this.getUserId(user),
                { email: user.email },
                { integrations }
            );
            setTimeout(() => {
                this.reportEvent(eventName, eventProps, integrations);
            }, 200);
        } else if (this.configProvider.config.environment !== 'production') {
            this.logger.debug(
                LogEvents.LogAnalyticForDev(
                    `Iterable event -  ${eventName}`,
                    eventProps
                )
            );
        }
    }

    setHBAnonymousId(id: string) {
        this.hbAnonymousId = id;
    }

    overrideSegmentAnonymousId(id: string, traits = {}) {
        if (!this.isSegmentAvailable() || !this.shouldTrackSegments()) {
            return;
        }

        window.analytics?.identify(undefined, traits, {
            anonymousId: id
        });
    }

    getUserId(user: User) {
        const userId = user?._id;
        if (!userId || userId === 'undefined' || userId === 'null') {
            return null;
        }

        return userId;
    }
}
